import { useSearchParams, useRouter } from "@/hooks/useRouter";
import { useEffect, useContext } from "react";
import { FiltersContext } from "@/contexts/FiltersContext";
import { useSearchChatCache } from "@/hooks/useSearchChatCache";

export const useFiltersContext = () => {
  const context = useContext(FiltersContext);
  if (!context)
    throw new Error("useFiltersContext must be used within FiltersProvider");
  return context;
};

export const useFilters = () => {
  const { filters, setFilters } = useFiltersContext();
  const { routerSearchParams: searchParams } = useSearchParams();
  const router = useRouter();
  const { saveToCache, initialFilters } = useSearchChatCache();

  // Initialize filters with the query params
  useEffect(() => {
    const projectIds = decodeURIComponent(searchParams.get("p") || "");
    let projects: string[] = [];

    if (projectIds) {
      projects = projectIds.split(",");
    } else if (initialFilters.projectIds) {
      projects = initialFilters.projectIds;
    }

    // URLSearchParams.get() already returns decoded values, no need for decodeURIComponent
    const agentNamesParam = searchParams.get("a") || "";
    let agents: string[] = [];

    if (agentNamesParam) {
      agents = agentNamesParam.split(",").filter((name) => name.trim() !== "");
    } else if (initialFilters.agentNames) {
      agents = initialFilters.agentNames;
    }

    const timeFilter =
      searchParams.get("t") ||
      (initialFilters.timeFilter as "today" | "week" | "month" | null) ||
      "";
    setFilters({
      projectIds: projects,
      timeFilter: timeFilter,
      agentNames: agents,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- we don't want to re-run this effect when the filters change

  const setProjectFilter = (projectId: string) => {
    const newProjectIds = new Set([...filters.projectIds, projectId]);
    const nextProjectIds = Array.from(newProjectIds);
    setFilters({
      ...filters,
      projectIds: nextProjectIds,
    });
    saveToCache({
      filters: {
        projectIds: nextProjectIds,
      },
    });
    return nextProjectIds;
  };

  const maybeUpdateProjectSearchParams = (projectIds: string[]) => {
    if (searchParams.get("q")) {
      const queryParams = new URLSearchParams(searchParams);
      if (projectIds.length === 0) {
        queryParams.delete("p");
      } else {
        queryParams.set("p", projectIds.join(","));
      }
      router.push(`/search?${queryParams.toString()}`);
    }
  };

  const removeProjectFilter = (projectId: string) => {
    const nextProjectIds = filters.projectIds.filter((id) => id !== projectId);
    setFilters({
      ...filters,
      projectIds: nextProjectIds,
    });
    maybeUpdateProjectSearchParams(nextProjectIds);
    saveToCache({
      filters: {
        projectIds: nextProjectIds,
      },
    });
    return nextProjectIds;
  };

  const toggleProjectFilter = (projectId: string) => {
    let nextProjectIds: string[] = filters.projectIds;
    if (filters.projectIds.includes(projectId)) {
      nextProjectIds = removeProjectFilter(projectId);
    } else {
      nextProjectIds = setProjectFilter(projectId);
    }
    saveToCache({
      filters: {
        projectIds: nextProjectIds,
      },
    });
    maybeUpdateProjectSearchParams(nextProjectIds);
  };

  const resetProjectsFilter = (projectIds: string[] = []) => {
    setFilters({
      ...filters,
      projectIds,
    });
    saveToCache({
      filters: {
        projectIds,
      },
    });
    maybeUpdateProjectSearchParams([]);
  };

  const maybeUpdateTimeSearchParams = (timeFilter: string) => {
    if (searchParams.get("q")) {
      const queryParams = new URLSearchParams(searchParams);
      if (timeFilter === "") {
        queryParams.delete("t");
      } else {
        queryParams.set("t", timeFilter);
      }
      router.push(`/search?${queryParams.toString()}`);
    }
  };

  const setTimeFilter = (timeFilter: string) => {
    setFilters({
      ...filters,
      timeFilter,
    });
    maybeUpdateTimeSearchParams(timeFilter);
    saveToCache({
      filters: {
        timeFilter: timeFilter as "today" | "week" | "month" | null,
      },
    });
  };

  const resetTimeFilter = () => {
    setFilters({
      ...filters,
      timeFilter: "",
    });
    maybeUpdateTimeSearchParams("");
    saveToCache({
      filters: {
        timeFilter: null,
      },
    });
  };

  const maybeUpdateAgentSearchParams = (agentNames: string[]) => {
    // Update URL if we're on the search page (has query param)
    // This ensures agents are reflected in the URL when user is already searching
    const hasQuery = searchParams.get("q");
    if (hasQuery) {
      const queryParams = new URLSearchParams(searchParams);
      if (agentNames.length === 0) {
        queryParams.delete("a");
      } else {
        // URLSearchParams.set() handles encoding automatically, so we don't need encodeURIComponent
        queryParams.set("a", agentNames.join(","));
      }
      router.push(`/search?${queryParams.toString()}`);
    }
    // If no query yet, agents will be included when user submits (handled in handleSubmit)
  };

  const setAgentName = (agentName: string) => {
    const newAgentNames = new Set([...filters.agentNames, agentName]);
    const nextAgentNames = Array.from(newAgentNames);
    setFilters({
      ...filters,
      agentNames: nextAgentNames,
    });
    maybeUpdateAgentSearchParams(nextAgentNames);
    saveToCache({
      filters: {
        agentNames: nextAgentNames,
      },
    });
    return nextAgentNames;
  };

  const removeAgentName = (agentName: string) => {
    const nextAgentNames = filters.agentNames.filter(
      (name) => name !== agentName
    );
    setFilters({
      ...filters,
      agentNames: nextAgentNames,
    });
    maybeUpdateAgentSearchParams(nextAgentNames);
    saveToCache({
      filters: {
        agentNames: nextAgentNames,
      },
    });
    return nextAgentNames;
  };

  const toggleAgentName = (agentName: string) => {
    let nextAgentNames: string[] = filters.agentNames;
    if (filters.agentNames.includes(agentName)) {
      nextAgentNames = removeAgentName(agentName);
    } else {
      nextAgentNames = setAgentName(agentName);
    }
    saveToCache({
      filters: {
        agentNames: nextAgentNames,
      },
    });
    maybeUpdateAgentSearchParams(nextAgentNames);
  };

  const resetAgentsFilter = (agentNames: string[] = []) => {
    setFilters({
      ...filters,
      agentNames,
    });
    saveToCache({
      filters: {
        agentNames,
      },
    });
    maybeUpdateAgentSearchParams([]);
  };

  const resetFilters = () => {
    setFilters({
      ...filters,
      projectIds: [],
      timeFilter: "",
      agentNames: [],
    });
    maybeUpdateTimeSearchParams("");
    maybeUpdateAgentSearchParams([]);
    saveToCache({
      filters: {
        projectIds: [],
        timeFilter: null,
        agentNames: [],
      },
    });
  };

  return {
    projectIds: filters.projectIds,
    timeFilter: filters.timeFilter,
    agentNames: filters.agentNames,
    /* Set projects filters */
    setProjectId: setProjectFilter,
    removeProjectId: removeProjectFilter,
    toggleProjectId: toggleProjectFilter,
    resetProjectsFilter,
    /* Set time filters */
    setTimeFilter,
    resetTimeFilter,
    /* Set agent filters */
    setAgentName,
    removeAgentName,
    toggleAgentName,
    resetAgentsFilter,
    /* Reset all filters */
    resetFilters,
  };
};
